import { translate } from "@atoms/atom-client/i18n";
import DxfParser, {
  type IArcEntity,
  type ICircleEntity,
  type IEntity,
  type ILineEntity,
  type ILwpolylineEntity,
  type IPolylineEntity,
} from "dxf-parser";
import MakerJS, { type IModel } from "makerjs";
import { MakerJSModel } from "../core/MakerJSModel";

const MAX_DXF_FILE_SIZE_BYTES = 2 * 1024 * 1024;
const BULGE_EPSILON = 1e-10;

interface DxfContourVertex {
  x: number;
  y: number;
  bulge?: number;
}

export class CutoutDxfConversionError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "CutoutDxfConversionError";
  }
}

interface ClosedContour {
  vertices: DxfContourVertex[];
}

export function svgPathToDxf(svgPath: string): string {
  const trimmed = svgPath.trim();
  if (!trimmed) {
    throw new CutoutDxfConversionError(
      translate("app.emerge.dxf.svgPathEmpty", "SVG-Pfad ist leer."),
    );
  }

  try {
    const model = MakerJS.importer.fromSVGPathData(trimmed, {
      bezierAccuracy: 0.5,
    });
    return MakerJS.exporter.toDXF(model, MakerJSModel.defaultDXFRenderOptions);
  } catch (error) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.svgPathInvalidForExport",
        "Ungültiger SVG-Pfad für DXF-Export.",
      ),
      error,
    );
  }
}

export function dxfToSvgPath(
  dxfContent: string,
  options?: { maxFileSizeBytes?: number },
): string {
  const trimmed = dxfContent.trim();
  if (!trimmed) {
    throw new CutoutDxfConversionError(
      translate("app.emerge.dxf.fileEmpty", "DXF-Datei ist leer."),
    );
  }

  const maxSize = options?.maxFileSizeBytes ?? MAX_DXF_FILE_SIZE_BYTES;
  if (trimmed.length > maxSize) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.fileTooLarge",
        "DXF-Datei ist zu groß (max. {{maxMb}} MB).",
        { maxMb: Math.round(maxSize / 1024 / 1024) },
      ),
    );
  }

  let parsed: ReturnType<DxfParser["parseSync"]>;
  try {
    const parser = new DxfParser();
    parsed = parser.parseSync(trimmed);
  } catch (error) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.fileUnreadable",
        "DXF-Datei konnte nicht gelesen werden.",
      ),
      error,
    );
  }

  if (!parsed?.entities?.length) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.noSupportedContours",
        "DXF enthält keine unterstützten Konturen (LWPOLYLINE, POLYLINE, ARC, CIRCLE, LINE).",
      ),
    );
  }

  const contours = extractClosedContours(parsed.entities);
  if (contours.length === 0) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.noClosedContours",
        "DXF enthält keine geschlossenen Konturen.",
      ),
    );
  }

  const rawPaths = contours.map((contour) =>
    contourVerticesToSvgPath(contour.vertices),
  );
  const combinedPath = rawPaths.join(" ");

  try {
    const model = MakerJS.importer.fromSVGPathData(combinedPath, {
      bezierAccuracy: 0.5,
    });
    const normalized = MakerJS.exporter.toSVGPathData(model, {
      origin: [0, 0],
      accuracy: 2,
      byLayers: false,
    });
    if (typeof normalized !== "string" || !normalized.trim()) {
      throw new CutoutDxfConversionError(
        translate(
          "app.emerge.dxf.contourNotConvertible",
          "DXF-Kontur konnte nicht in einen SVG-Pfad umgewandelt werden.",
        ),
      );
    }
    return normalized;
  } catch (error) {
    if (error instanceof CutoutDxfConversionError) throw error;
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.contourNotConvertible",
        "DXF-Kontur konnte nicht in einen SVG-Pfad umgewandelt werden.",
      ),
      error,
    );
  }
}

export function downloadCutoutDxf(svgPath: string, filename: string): void {
  const dxf = svgPathToDxf(svgPath);
  const blob = new Blob([dxf], { type: "application/dxf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".dxf") ? filename : `${filename}.dxf`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function addLwPolylineContour(
  lw: ILwpolylineEntity,
  contours: ClosedContour[],
): void {
  if (!lw.vertices?.length) return;
  if (!lw.shape) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.openLwPolyline",
        "DXF enthält eine offene LWPOLYLINE. Ausschnitte müssen geschlossen sein.",
      ),
    );
  }
  contours.push({ vertices: lw.vertices });
}

function addPolylineContour(
  polyline: IPolylineEntity,
  contours: ClosedContour[],
): void {
  if (!polyline.vertices?.length) return;
  if (!polyline.shape) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.openPolyline",
        "DXF enthält eine offene POLYLINE. Ausschnitte müssen geschlossen sein.",
      ),
    );
  }
  contours.push({
    vertices: polyline.vertices.map((vertex) => ({
      x: vertex.x,
      y: vertex.y,
      bulge: vertex.bulge ?? 0,
    })),
  });
}

function addArcEntity(
  arc: IArcEntity,
  contours: ClosedContour[],
  chainSegments: ChainSegment[],
): void {
  if (isFullCircleArc(arc)) {
    contours.push({
      vertices: circleToContourVertices({
        center: arc.center,
        radius: arc.radius,
      } as ICircleEntity),
    });
    return;
  }
  chainSegments.push(arcToChainSegment(arc));
}

function addLineEntity(line: ILineEntity, chainSegments: ChainSegment[]): void {
  const [start, end] = line.vertices ?? [];
  if (!start || !end) return;
  chainSegments.push({
    start: { x: start.x, y: start.y },
    end: { x: end.x, y: end.y },
    bulge: 0,
  });
}

function processDxfEntity(
  entity: IEntity,
  contours: ClosedContour[],
  chainSegments: ChainSegment[],
): void {
  switch (entity.type) {
    case "LWPOLYLINE":
      addLwPolylineContour(entity as ILwpolylineEntity, contours);
      break;
    case "POLYLINE":
      addPolylineContour(entity as IPolylineEntity, contours);
      break;
    case "CIRCLE":
      contours.push({
        vertices: circleToContourVertices(entity as ICircleEntity),
      });
      break;
    case "ARC":
      addArcEntity(entity as IArcEntity, contours, chainSegments);
      break;
    case "LINE":
      addLineEntity(entity as ILineEntity, chainSegments);
      break;
    default:
      break;
  }
}

function extractClosedContours(entities: IEntity[]): ClosedContour[] {
  const contours: ClosedContour[] = [];
  const chainSegments: ChainSegment[] = [];

  for (const entity of entities) {
    processDxfEntity(entity, contours, chainSegments);
  }

  contours.push(...assembleChainLoops(chainSegments));

  return contours;
}

interface Point2D {
  x: number;
  y: number;
}

interface ChainSegment {
  start: Point2D;
  end: Point2D;
  /** DXF bulge from start to end; 0 for straight LINE segments. */
  bulge: number;
}

const LINE_JOIN_EPSILON = 1e-6;
const FULL_CIRCLE_ARC_EPSILON = 1e-6;

function pointsEqual(a: Point2D, b: Point2D): boolean {
  return (
    Math.abs(a.x - b.x) < LINE_JOIN_EPSILON &&
    Math.abs(a.y - b.y) < LINE_JOIN_EPSILON
  );
}

function arcEndpoints(arc: IArcEntity): {
  start: Point2D;
  end: Point2D;
  sweepRad: number;
} {
  // dxf-parser converts DXF group 50/51 (degrees) to radians before we see them.
  const startRad = arc.startAngle;
  const endRad = arc.endAngle;
  const start = {
    x: arc.center.x + arc.radius * Math.cos(startRad),
    y: arc.center.y + arc.radius * Math.sin(startRad),
  };
  const end = {
    x: arc.center.x + arc.radius * Math.cos(endRad),
    y: arc.center.y + arc.radius * Math.sin(endRad),
  };
  let sweepRad = endRad - startRad;
  while (sweepRad <= 0) sweepRad += 2 * Math.PI;
  return { start, end, sweepRad };
}

function isFullCircleArc(arc: IArcEntity): boolean {
  const { start, end, sweepRad } = arcEndpoints(arc);
  return (
    sweepRad >= 2 * Math.PI - FULL_CIRCLE_ARC_EPSILON || pointsEqual(start, end)
  );
}

function arcToChainSegment(arc: IArcEntity): ChainSegment {
  const { start, end, sweepRad } = arcEndpoints(arc);
  return {
    start,
    end,
    bulge: Math.tan(sweepRad / 4),
  };
}

/**
 * Chains loose LINE and ARC segments (as exported by many CAD tools) into closed
 * contours by matching shared endpoints. Bulge values are preserved for ARC
 * segments; reversing traversal negates the bulge per DXF convention.
 */
function assembleChainLoops(segments: ChainSegment[]): ClosedContour[] {
  if (segments.length === 0) return [];

  const remaining = [...segments];
  const loops: ClosedContour[] = [];

  while (remaining.length > 0) {
    const first = remaining.shift();
    if (!first) continue;

    const loopStart = first.start;
    const vertices: DxfContourVertex[] = [
      { x: first.start.x, y: first.start.y, bulge: first.bulge },
    ];
    let openEnd = first.end;

    while (!pointsEqual(openEnd, loopStart)) {
      const nextIndex = remaining.findIndex(
        (segment) =>
          pointsEqual(segment.start, openEnd) ||
          pointsEqual(segment.end, openEnd),
      );

      if (nextIndex === -1) {
        throw new CutoutDxfConversionError(
          translate(
            "app.emerge.dxf.openLinesArcs",
            "DXF enthält LINE- oder ARC-Elemente, die keine geschlossene Kontur bilden. Ausschnitte müssen geschlossen sein.",
          ),
        );
      }

      const [next] = remaining.splice(nextIndex, 1);
      if (!next) break;

      const forward = pointsEqual(next.start, openEnd);
      vertices.push({
        x: openEnd.x,
        y: openEnd.y,
        bulge: forward ? next.bulge : -next.bulge,
      });
      openEnd = forward ? next.end : next.start;
    }

    loops.push({ vertices });
  }

  return loops;
}

function circleToContourVertices(circle: ICircleEntity): DxfContourVertex[] {
  const cx = circle.center.x;
  const cy = circle.center.y;
  const r = circle.radius;
  // A full circle is two 180° arcs; DXF bulge = tan(θ/4) = tan(π/4) = 1.
  return [
    { x: cx - r, y: cy, bulge: 1 },
    { x: cx + r, y: cy, bulge: 1 },
  ];
}

function contourVerticesToSvgPath(vertices: DxfContourVertex[]): string {
  if (vertices.length < 2) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.tooFewPoints",
        "DXF-Kontur hat zu wenige Punkte.",
      ),
    );
  }

  const first = vertices[0];
  if (!first) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.tooFewPoints",
        "DXF-Kontur hat zu wenige Punkte.",
      ),
    );
  }
  let path = `M ${formatCoord(first.x)} ${formatCoord(first.y)}`;

  for (let index = 0; index < vertices.length; index++) {
    const current = vertices[index];
    const next = vertices[(index + 1) % vertices.length];
    if (!current || !next) continue;
    const bulge = current.bulge ?? 0;

    if (Math.abs(bulge) < BULGE_EPSILON) {
      path += ` L ${formatCoord(next.x)} ${formatCoord(next.y)}`;
      continue;
    }

    const arc = bulgeToSvgArc(current.x, current.y, next.x, next.y, bulge);
    path += ` A ${formatCoord(arc.rx)} ${formatCoord(arc.ry)} 0 ${arc.largeArc} ${arc.sweep} ${formatCoord(next.x)} ${formatCoord(next.y)}`;
  }

  return `${path} Z`;
}

function bulgeToSvgArc(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  bulge: number,
): { rx: number; ry: number; largeArc: 0 | 1; sweep: 0 | 1 } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const chord = Math.hypot(dx, dy);
  if (chord < BULGE_EPSILON) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.invalidArcs",
        "DXF enthält ungültige Bogensegmente.",
      ),
    );
  }

  const theta = 4 * Math.atan(bulge);
  const radius = Math.abs(chord / (2 * Math.sin(theta / 2)));

  return {
    rx: radius,
    ry: radius,
    largeArc: Math.abs(theta) > Math.PI ? 1 : 0,
    sweep: bulge > 0 ? 1 : 0,
  };
}

function formatCoord(value: number): string {
  return Number.parseFloat(value.toFixed(4)).toString();
}

export function assertSvgPathImportable(svgPath: string): IModel {
  try {
    return MakerJS.importer.fromSVGPathData(svgPath.trim(), {
      bezierAccuracy: 0.5,
    });
  } catch (error) {
    throw new CutoutDxfConversionError(
      translate(
        "app.emerge.dxf.importedSvgInvalid",
        "Importierter SVG-Pfad ist ungültig.",
      ),
      error,
    );
  }
}
